/**
 * Cabecalho superior global do LegisCity (54px).
 *
 * Oferece navegacao acessivel e discreta entre o Mapa (Home), Estatisticas,
 * Como Funciona e Sobre, sem poluir a experiencia cartografica.
 *
 * @module components/Navbar
 */

import { useState } from "react";
import BrandLogo from "./BrandLogo";
import ThemeToggle from "./ThemeToggle";

export default function Navbar({
  currentPath,
  navigate,
  themeMode,
  toggleTheme,
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isDark = themeMode === "dark";

  const navItems = [
    {
      id: "mapa",
      label: "Mapa",
      path: "/",
      group: "primary",
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
          <line x1="9" y1="3" x2="9" y2="18" />
          <line x1="15" y1="6" x2="15" y2="21" />
        </svg>
      ),
    },
    {
      id: "estatisticas",
      label: "Estatísticas",
      path: "/estatisticas",
      group: "primary",
      icon: (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.1"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-chart-no-axes-combined"
        >
          <path d="M12 16v5" />
          <path d="M16 14.639V21" />
          <path d="M20 10.656V21" />
          <path d="m22 3-8.646 8.646a.5.5 0 0 1-.708 0L9.354 8.354a.5.5 0 0 0-.707 0L2 15" />
          <path d="M4 18.463V21" />
          <path d="M8 14.656V21" />
        </svg>
      ),
    },
    {
      id: "como-funciona",
      label: "Como funciona",
      path: "/como-funciona",
      group: "secondary",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      ),
    },
    {
      id: "sobre",
      label: "Sobre",
      path: "/sobre",
      group: "secondary",
      icon: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="21" x2="21" y2="21" />
          <line x1="6" y1="21" x2="6" y2="11" />
          <line x1="10" y1="21" x2="10" y2="11" />
          <line x1="14" y1="21" x2="14" y2="11" />
          <line x1="18" y1="21" x2="18" y2="11" />
          <polygon points="12 3 2 10 22 10" />
        </svg>
      ),
    },
  ];

  const primaryNavItems = navItems.filter((item) => item.group === "primary");
  const secondaryNavItems = navItems.filter((item) => item.group === "secondary");

  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const isRouteActive = (itemPath) => {
    if (itemPath === "/") return currentPath === "/";
    return currentPath.startsWith(itemPath);
  };

  return (
    <header
      style={{
        height: 54,
        maxHeight: 54,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 18px",
        background: "var(--bg-glass)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: "1px solid var(--border-color)",
        position: "relative",
        zIndex: 1100,
        boxSizing: "border-box",
        userSelect: "none",
      }}
    >
      {/* ── Lado Esquerdo: Identidade Visual & Escopo Municipal ── */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <button
          type="button"
          onClick={() => handleNavClick("/")}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 0,
            display: "flex",
            alignItems: "center",
            overflow: "visible",
            flexShrink: 0,
          }}
          title="LegisCity — Retornar ao mapa"
        >
          <BrandLogo variant="compact" height={32} isDark={isDark} />
        </button>

        <span
          aria-hidden="true"
          style={{
            width: 1,
            height: 18,
            background: "var(--border-color)",
            flexShrink: 0,
          }}
        />

        <div
          style={{
            height: 24,
            display: "inline-flex",
            alignItems: "center",
            gap: 4.5,
            padding: "0 9px",
            background: "var(--bg-surface-elevated)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--radius-full)",
            fontSize: "11px",
            fontWeight: 600,
            color: "var(--text-secondary)",
            lineHeight: 1,
            whiteSpace: "nowrap",
            boxSizing: "border-box",
          }}
          title="Dados abertos da Câmara Municipal de Artur Nogueira, SP"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.3"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: "var(--accent-primary)", flexShrink: 0 }}
          >
            <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span>Artur Nogueira, SP</span>
        </div>
      </div>

      {/* ── Centro: Seletor de Modo Principal (Segmented Switcher: Mapa | Estatísticas) ── */}
      <nav
        aria-label="Visualização principal"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          padding: 3,
          borderRadius: "var(--radius-md)",
          background: "var(--bg-surface-elevated)",
          border: "1px solid var(--border-color)",
          flexShrink: 0,
          boxSizing: "border-box",
        }}
        className="navbar-desktop-links"
      >
        {primaryNavItems.map((item) => {
          const active = isRouteActive(item.path);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleNavClick(item.path)}
              style={{
                height: 28,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "0 13px",
                borderRadius: 7,
                background: active ? "var(--bg-surface)" : "transparent",
                color: active ? "var(--accent-primary)" : "var(--text-secondary)",
                boxShadow: active ? "var(--shadow-sm)" : "none",
                border: "none",
                fontSize: "var(--text-label)",
                fontWeight: active ? 600 : 500,
                cursor: "pointer",
                transition: "all var(--transition-fast)",
                boxSizing: "border-box",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.color = "var(--text-primary)";
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.color = "var(--text-secondary)";
                }
              }}
            >
              <span style={{ display: "flex", alignItems: "center" }}>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* ── Lado Direito: Guias Institucionais + Alternador de Tema ── */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10 }}>
        <div
          className="navbar-desktop-links"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {secondaryNavItems.map((item) => {
            const active = isRouteActive(item.path);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavClick(item.path)}
                style={{
                  height: 32,
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "0 10px",
                  borderRadius: "var(--radius-sm)",
                  background: active ? "var(--accent-light)" : "transparent",
                  color: active ? "var(--accent-primary)" : "var(--text-secondary)",
                  border: "none",
                  fontSize: "var(--text-label)",
                  fontWeight: active ? 600 : 500,
                  cursor: "pointer",
                  transition: "all var(--transition-fast)",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = "var(--border-subtle)";
                    e.currentTarget.style.color = "var(--text-primary)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "var(--text-secondary)";
                  }
                }}
              >
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        <span
          className="navbar-desktop-links"
          aria-hidden="true"
          style={{
            width: 1,
            height: 18,
            background: "var(--border-color)",
            flexShrink: 0,
          }}
        />

        <ThemeToggle isDarkMode={isDark} onToggle={toggleTheme} />

        {/* Botão de Menu para Telas Pequenas */}
        <button
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="navbar-mobile-toggle"
          style={{
            background: "transparent",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--radius-sm)",
            width: 34,
            height: 34,
            display: "none",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-primary)",
            cursor: "pointer",
          }}
          title={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
        >
          {mobileMenuOpen ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>
      </div>

      {/* ── Menu Suspenso Mobile ── */}
      {mobileMenuOpen && (
        <div
          style={{
            position: "absolute",
            top: 54,
            left: 0,
            right: 0,
            background: "var(--bg-surface)",
            borderBottom: "1px solid var(--border-color)",
            boxShadow: "var(--shadow-glass)",
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            zIndex: 1100,
          }}
        >
          {navItems.map((item) => {
            const active = isRouteActive(item.path);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNavClick(item.path)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  borderRadius: "var(--radius-sm)",
                  background: active ? "var(--accent-light)" : "transparent",
                  color: active ? "var(--accent-primary)" : "var(--text-primary)",
                  border: "1px solid var(--border-color)",
                  fontSize: "var(--text-body-sm)",
                  fontWeight: active ? 700 : 500,
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </header>
  );
}
